import { useEffect, type CSSProperties, type ReactNode } from 'react'

import { cn } from '../../../../lib/utils'
import { createNadDebug } from '../../../../utils/debugLog'

const log = createNadDebug('dw:meta-loading-ui')

export type MetaLoadingState =
  | 'running'
  | 'local-grace'
  | 'pending-execution'
  | 'pending-cost'
  | 'processing'

export type MetaLoadingDescriptor = {
  message: string
  detail?: string
  nodeType: string
  loadingState: MetaLoadingState
}

type MetaLoadingSurfaceProps = MetaLoadingDescriptor & {
  projectId?: string | null
  nodeId: string
  aspectRatio?: string
  presentation: 'full' | 'lightweight'
  children?: ReactNode
  className?: string
  style?: CSSProperties
}

/** Full-preview Meta gradient treatment from Workplace post 1649461840144446. */
export function MetaLoadingSurface({
  message,
  detail,
  nodeType,
  loadingState,
  projectId,
  nodeId,
  aspectRatio,
  presentation,
  children,
  className,
  style,
}: MetaLoadingSurfaceProps) {
  useEffect(() => {
    log({
      event: 'node-gradient-loading-shown',
      trace_id: `meta-loading:${projectId ?? 'local'}:${nodeId}`,
      project_id: projectId ?? null,
      node_id: nodeId,
      node_type: nodeType,
      loading_state: loadingState,
      aspect_ratio: aspectRatio ?? null,
      presentation,
    })
    return () => {
      log({
        event: 'node-gradient-loading-hidden',
        trace_id: `meta-loading:${projectId ?? 'local'}:${nodeId}`,
        project_id: projectId ?? null,
        node_id: nodeId,
        node_type: nodeType,
        loading_state: loadingState,
        presentation,
      })
    }
  }, [aspectRatio, loadingState, nodeId, nodeType, presentation, projectId])

  return (
    <div
      className={cn(
        'dw-meta-loading-surface absolute inset-0 flex flex-col items-center justify-center overflow-hidden px-4 text-center',
        className,
      )}
      data-testid="meta-loading-surface"
      data-loading-presentation={presentation}
      data-loading-node-type={nodeType}
      data-loading-state={loadingState}
      aria-busy="true"
      style={style}
    >
      <span
        className="relative z-10 max-w-48 font-loading text-sm font-normal leading-relaxed text-white/90"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="block">{message}</span>
        {detail ? <span className="mt-1 block text-xxs font-normal text-white/70">{detail}</span> : null}
      </span>
      {children ? <div className="relative z-10 flex flex-col items-center">{children}</div> : null}
    </div>
  )
}
